// frontend/src/services/surveyService.js
import axios from '../axiosConfig';

/**
 * Survey Service
 * Handles all survey-related API calls with caching and error handling
 */
class SurveyService {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    }

    /**
     * Check if survey menu should be visible for an entity
     * Implements three-tier visibility rules (same as notifications)
     * @param {number} entityId - Entity ID
     * @returns {Promise<Object>} Visibility data
     */
    async checkMenuVisibility(entityId) {
        const cacheKey = `menu_visibility_${entityId}`;
        
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                return cached.data;
            }
        }
        
        try {
            const response = await axios.get(`/api/surveys/menu-visibility/${entityId}`);
            const data = response.data;
            
            // Cache the result
            this.cache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });
            
            return data;
        } catch (error) {
            console.error('Error checking survey visibility:', error);
            return { 
                success: false,
                visible: false,
                channels: {}
            };
        }
    }

    /**
     * Get survey preferences for an entity
     */
    async getSurveyPreferences(entityId) {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/preferences`);
            // Backend returns preferences directly in response.data.preferences
            return response.data.preferences || {};
        } catch (error) {
            console.error('Error fetching survey preferences:', error);
            return {};
        }
    }

    /**
     * Update survey preferences
     */
    async updateSurveyPreferences(entityId, preferences) {
        try {
            const response = await axios.put(`/api/surveys/${entityId}/preferences`, preferences);
            this.clearCache();
            return response.data;
        } catch (error) {
            console.error('Error updating survey preferences:', error);
            throw error;
        }
    }

    /**
     * Get survey for end users to view/take
     * Returns questions in user's preferred language
     */
    async getSurveyForViewing(entityId, surveyType) {
        const cacheKey = `view_${entityId}_${surveyType}`;
        const cached = this.getFromCache(cacheKey);
        if (cached) return cached;

        try {
            const response = await axios.get(`/api/surveys/${entityId}/view/${surveyType}`);
            // Backend returns the survey data in response.data
            const data = response.data?.data || null;
            this.setCache(cacheKey, data);
            return data;
        } catch (error) {
            console.error('Error fetching survey for viewing:', error);
            return null;
        }
    }

    /**
     * Get survey configuration (questions) for a specific type (admin only)
     * Returns questions in entity's default language for consistency
     */
    async getSurveyConfiguration(entityId, surveyType) {
        const cacheKey = `config_${entityId}_${surveyType}`;
        const cached = this.getFromCache(cacheKey);
        if (cached) return cached;

        try {
            const response = await axios.get(`/api/surveys/${entityId}/configuration/${surveyType}`);
            // Backend returns the configuration directly in response.data
            const data = response.data || null;
            this.setCache(cacheKey, data);
            return data;
        } catch (error) {
            console.error('Error fetching survey configuration:', error);
            return null;
        }
    }

    /**
     * Update survey configuration (questions)
     */
    async updateSurveyConfiguration(entityId, surveyType, configuration) {
        try {
            const response = await axios.put(
                `/api/surveys/${entityId}/configuration/${surveyType}`,
                configuration
            );
            this.clearCache();
            return response.data;
        } catch (error) {
            console.error('Error updating survey configuration:', error);
            throw error;
        }
    }

    /**
     * Get available surveys for current user
     */
    async getAvailableSurveys(entityId) {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/available`);
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching available surveys:', error);
            return [];
        }
    }

    /**
     * Check for triggered surveys (e.g., after trip completion)
     */
    async checkSurveyTrigger(entityId, triggerData) {
        try {
            const response = await axios.post(`/api/surveys/${entityId}/check-trigger`, triggerData);
            return response.data.data || [];
        } catch (error) {
            console.error('Error checking survey trigger:', error);
            return [];
        }
    }

    /**
     * Submit survey responses
     */
    async submitSurveyResponse(entityId, surveyId, responses) {
        try {
            const response = await axios.post(`/api/surveys/${entityId}/response`, {
                survey_id: surveyId,
                responses
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting survey response:', error);
            throw error;
        }
    }

    /**
     * Get survey analytics
     */
    async getSurveyAnalytics(entityId, surveyType, dateRange = {}) {
        const cacheKey = `analytics_${entityId}_${surveyType}_${JSON.stringify(dateRange)}`;
        const cached = this.getFromCache(cacheKey);
        if (cached) return cached;

        try {
            const response = await axios.get(`/api/surveys/${entityId}/analytics/${surveyType}`, {
                params: dateRange
            });
            const data = response.data.data || {};
            this.setCache(cacheKey, data);
            return data;
        } catch (error) {
            console.error('Error fetching survey analytics:', error);
            return {};
        }
    }

    /**
     * Get question-level analytics
     */
    async getQuestionAnalytics(entityId, questionId, dateRange = {}) {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/analytics/question/${questionId}`, {
                params: dateRange
            });
            return response.data.data || {};
        } catch (error) {
            console.error('Error fetching question analytics:', error);
            return {};
        }
    }

    /**
     * Get text responses for a question
     */
    async getTextResponses(entityId, questionId, page = 1, limit = 20) {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/responses/text/${questionId}`, {
                params: { page, limit }
            });
            return response.data.data || { responses: [], total: 0 };
        } catch (error) {
            console.error('Error fetching text responses:', error);
            return { responses: [], total: 0 };
        }
    }

    /**
     * Check if user can manage surveys
     */
    async checkSurveyPermissions(entityId) {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/permissions`);
            // Parse the response to match expected format
            if (response.data && response.data.success) {
                const data = response.data;
                return {
                    canManageService: data.permissions?.service?.canManage || false,
                    canManageSystem: data.permissions?.system?.canManage || false,
                    canViewAnalytics: data.canViewAny || false,
                    canManageAny: data.canManageAny || false,
                    canViewAny: data.canViewAny || false,
                    permissions: data.permissions
                };
            }
            return {
                canManageService: false,
                canManageSystem: false,
                canViewAnalytics: false
            };
        } catch (error) {
            console.error('Error checking survey permissions:', error);
            return {
                canManageService: false,
                canManageSystem: false,
                canViewAnalytics: false
            };
        }
    }

    /**
     * Export survey data
     */
    async exportSurveyData(entityId, surveyType, format = 'csv', dateRange = {}) {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/export/${surveyType}`, {
                params: { format, ...dateRange },
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `survey_${surveyType}_${Date.now()}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            return true;
        } catch (error) {
            console.error('Error exporting survey data:', error);
            return false;
        }
    }

    /**
     * Cache management utilities
     */
    getFromCache(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            return cached.data;
        }
        this.cache.delete(key);
        return null;
    }

    setCache(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    clearCache() {
        this.cache.clear();
    }
}

export default new SurveyService();